import {
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type RefObject,
} from "react"

import { formatHoverTime, HOVER_TIME_LABEL_CLASS } from "./hoverTimeLabel"
import { measureAudioTimeline, timelineLineStyle } from "./measureAudioTimeline"

export function useAudioTimelineHover(
  root: RefObject<HTMLDivElement | null>,
  enabled: boolean,
  layoutKey: string,
  /** Hears the moment under the pointer, and null once it leaves the tracks. */
  onTimeChange?: (seconds: number | null) => void,
) {
  const [preview, setPreview] = useState<{
    left: number
    top: number
    height: number
    time: number
    alignRight: boolean
    layoutKey: string
  } | null>(null)
  const reportedRef = useRef<number | null>(null)
  const report = (seconds: number | null) => {
    if (reportedRef.current === seconds) {
      return
    }
    reportedRef.current = seconds
    onTimeChange?.(seconds)
  }
  const clearPreview = () => {
    setPreview(null)
    report(null)
  }
  // A timeline switched off, relaid or taken away under the pointer never
  // hears it leave, so it retires the hover it reported. So does one whose
  // listener is swapped out mid-hover: the old listener hears it end, and the
  // new one hears nothing until the pointer moves.
  const reportRef = useRef(report)
  const listenerRef = useRef(onTimeChange)
  useEffect(() => {
    reportRef.current = report
    const previous = listenerRef.current
    listenerRef.current = onTimeChange
    if (previous !== onTimeChange && reportedRef.current != null) {
      reportedRef.current = null
      previous?.(null)
    }
  })
  useEffect(() => {
    if (!enabled) {
      reportRef.current(null)
    }
  }, [enabled])
  // A new layout moves the tracks out from under a still pointer, and the
  // preview it was showing is dropped with it; the moment reported goes too.
  useEffect(() => {
    reportRef.current(null)
  }, [layoutKey])
  useEffect(() => () => reportRef.current(null), [])
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const container = root.current
    if (!enabled || !container || event.pointerType === "touch") {
      clearPreview()
      return
    }
    const { tracks, top, bottom, containerRect } =
      measureAudioTimeline(container)
    if (event.clientY < top || event.clientY > bottom || tracks.length === 0) {
      clearPreview()
      return
    }
    const distance = (track: (typeof tracks)[number]) =>
      Math.max(track.top - event.clientY, event.clientY - track.bottom, 0)
    const track = tracks.reduce((nearest, next) =>
      distance(next) < distance(nearest) ? next : nearest,
    )
    if (event.clientX < track.rect.left || event.clientX > track.rect.right) {
      clearPreview()
      return
    }
    const time =
      ((event.clientX - track.rect.left) / track.rect.width) * track.duration
    setPreview({
      left: event.clientX - containerRect.left - container.clientLeft,
      top: top - containerRect.top - container.clientTop,
      height: bottom - top,
      time,
      alignRight: track.rect.right - event.clientX < 90,
      layoutKey,
    })
    report(time)
  }
  return {
    onPointerMove,
    onPointerLeave: clearPreview,
    onScrollCapture: clearPreview,
    preview:
      enabled && preview?.layoutKey === layoutKey ? (
        <div
          aria-hidden="true"
          data-testid="audio-timeline-hover"
          className="pointer-events-none absolute z-20 border-l border-coval-text-muted"
          style={timelineLineStyle(preview.left, preview.top, preview.height)}
        >
          <span
            className={`absolute top-0 ${HOVER_TIME_LABEL_CLASS}`}
            style={{
              transform: preview.alignRight ? "translateX(-100%)" : undefined,
            }}
          >
            {formatHoverTime(preview.time)}
          </span>
        </div>
      ) : null,
  }
}
